import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles, categories } from '@/mocks/articles';
import { useState, useMemo } from 'react';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSub, setActiveSub] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('latest');

  const currentCategory = useMemo(() => {
    const mainCat = categories.find((c) => c.slug === slug);
    if (mainCat) return mainCat;
    for (const cat of categories) {
      const sub = cat.subCategories.find((s) => s.slug === slug);
      if (sub) return { ...cat, subMatch: sub };
    }
    return null;
  }, [slug]);

  const filteredArticles = useMemo(() => {
    if (!currentCategory) return [];

    let result = articles;
    if ('subMatch' in currentCategory && currentCategory.subMatch) {
      result = result.filter((a) => a.categorySlug === currentCategory.slug);
    } else if (currentCategory.slug) {
      result = result.filter((a) => a.categorySlug === currentCategory.slug);
    }

    if (activeSub !== 'all') {
      result = result.filter((a) => a.tags.some((tag) => tag === activeSub));
    }

    if (sortBy === 'latest') {
      result = [...result].sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());
    } else {
      result = [...result].sort((a, b) => b.views - a.views);
    }

    return result;
  }, [currentCategory, activeSub, sortBy]);

  const allTags = useMemo(() => {
    const tags = new Set<string>();
    filteredArticles.forEach((a) => a.tags.forEach((t) => tags.add(t)));
    return Array.from(tags);
  }, [filteredArticles]);

  const displayName = useMemo(() => {
    if (!currentCategory) return '';
    if ('subMatch' in currentCategory && currentCategory.subMatch) {
      return currentCategory.subMatch.name;
    }
    return currentCategory.name;
  }, [currentCategory]);

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${displayName} - 国产一区二区精选内容导航平台`,
          description: currentCategory.description || `国产一区二区${displayName}内容专区，精选优质国产内容资源。`,
          url: `https://www.guochanyiqu.com/category/${slug}`,
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Category Header */}
          <section className="pt-20 pb-8 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: displayName },
                ]}
              />
              <div className="mt-4">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  {displayName}
                </h1>
                <p className="text-sm text-foreground-600 max-w-2xl leading-relaxed">
                  {currentCategory.description}
                </p>
              </div>

              {'subCategories' in currentCategory && currentCategory.subCategories && (
                <div className="flex flex-wrap items-center gap-2 mt-5">
                  <button
                    onClick={() => setActiveSub('all')}
                    className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all cursor-pointer ${
                      activeSub === 'all'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 border border-background-300 hover:border-primary-300'
                    }`}
                  >
                    全部
                  </button>
                  {currentCategory.subCategories.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => setActiveSub(sub.name)}
                      className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all cursor-pointer ${
                        activeSub === sub.name
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-50 text-foreground-600 border border-background-300 hover:border-primary-300'
                      }`}
                    >
                      {sub.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Content Area */}
          <section className="py-8 md:py-10">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
                <p className="text-sm text-foreground-500">
                  共找到 <strong className="text-foreground-950">{filteredArticles.length}</strong> 篇内容
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500">排序：</span>
                  <button
                    onClick={() => setSortBy('latest')}
                    className={`px-3 py-1 rounded-full text-xs whitespace-nowrap transition-all cursor-pointer ${
                      sortBy === 'latest'
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    最新发布
                  </button>
                  <button
                    onClick={() => setSortBy('popular')}
                    className={`px-3 py-1 rounded-full text-xs whitespace-nowrap transition-all cursor-pointer ${
                      sortBy === 'popular'
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    最多浏览
                  </button>
                </div>
              </div>

              {allTags.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  {allTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setActiveSub(activeSub === tag ? 'all' : tag)}
                      className={`px-3 py-1 rounded-full text-xs whitespace-nowrap transition-all cursor-pointer ${
                        activeSub === tag
                          ? 'bg-accent-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 border border-background-200 hover:border-accent-300'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              )}

              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-inbox-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500">当前筛选条件下暂无内容</p>
                  <button
                    onClick={() => { setActiveSub('all'); setSortBy('latest'); }}
                    className="mt-3 text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
                  >
                    清除筛选条件
                  </button>
                </div>
              )}

              <div className="mt-12 pt-8 border-t border-background-200">
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">浏览其他分类</h3>
                <div className="flex flex-wrap gap-3">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`px-5 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                        cat.slug === slug
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200'
                      }`}
                    >
                      <i className={`${cat.icon} mr-1.5`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}